import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    listInvestorNotes,
    createInvestorNote,
    updateInvestorNote,
    deleteInvestorNote,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasFeature, tierLabel } from "@/lib/tier";
import { StickyNote, Plus, Trash2, Edit2, Lock, ArrowUpRight, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * InvestorNotes — private per-report investor notes panel.
 *
 * Renders inside the report view for users with `investor_notes` feature.
 * Lite users see a calm locked teaser linking to /pricing. Anonymous /
 * share viewers receive nothing (private to owner).
 *
 * Cost-Safe: pure DB CRUD. Never triggers AI.
 */
export default function InvestorNotes({ reportId, isSample }) {
    const { user } = useAuth();
    const canUse = hasFeature(user, "investor_notes");
    const [notes, setNotes] = useState([]);
    const [locked, setLocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user || isSample || !reportId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const r = await listInvestorNotes({ report_id: reportId });
                if (cancelled) return;
                setNotes(r.notes || []);
                setLocked(!!r.locked);
            } catch {
                if (!cancelled) setLocked(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [reportId, user, isSample]);

    if (!user || isSample) return null;

    if (locked || !canUse) {
        return (
            <div
                className="bl-card p-5 md:p-6 mt-6"
                data-testid="investor-notes-locked"
            >
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center">
                        <Lock className="w-4 h-4 text-bl-primary" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-display font-semibold text-bl text-lg">
                            Private investor notes
                        </h3>
                        <p className="mt-1 text-sm text-bl-muted leading-relaxed">
                            Save private notes per property — "Roof quote needed",
                            "Seller seems motivated", "Verify foundation before
                            offer". Available on {tierLabel("investor_pro")}.
                        </p>
                        <Link
                            to="/pricing"
                            className="bl-btn-secondary text-sm mt-3 inline-flex"
                            data-testid="investor-notes-upgrade-cta"
                        >
                            Upgrade to {tierLabel("investor_pro")}{" "}
                            <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const onCreate = async () => {
        const t = draft.trim();
        if (!t) return;
        setSubmitting(true);
        try {
            const created = await createInvestorNote({
                report_id: reportId,
                note: t,
            });
            setNotes((n) => [created, ...n]);
            setDraft("");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not save note.");
        } finally {
            setSubmitting(false);
        }
    };

    const onSaveEdit = async (noteId) => {
        const t = editingText.trim();
        if (!t) return;
        try {
            const updated = await updateInvestorNote(noteId, { note: t });
            setNotes((n) => n.map((x) => (x.note_id === noteId ? updated : x)));
            setEditingId(null);
            setEditingText("");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not update.");
        }
    };

    const onDelete = async (noteId) => {
        try {
            await deleteInvestorNote(noteId);
            setNotes((n) => n.filter((x) => x.note_id !== noteId));
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not delete.");
        }
    };

    return (
        <div
            className="bl-card p-5 md:p-6 mt-6"
            data-testid="investor-notes-panel"
            data-toc-section="investor-notes"
        >
            <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center">
                    <StickyNote className="w-4 h-4 text-bl-primary" />
                </div>
                <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                        Investor workflow
                    </div>
                    <h3 className="font-display font-semibold text-bl text-lg leading-tight">
                        Private notes
                    </h3>
                </div>
            </div>

            <div className="flex gap-2">
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Add a private note (e.g. 'Roof quote needed before offer')…"
                    rows={2}
                    className="flex-1 px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                    data-testid="investor-notes-input"
                    maxLength={2000}
                />
                <button
                    onClick={onCreate}
                    disabled={!draft.trim() || submitting}
                    className="bl-btn-primary text-sm self-start"
                    data-testid="investor-notes-save-btn"
                >
                    <Plus className="w-4 h-4" /> Add
                </button>
            </div>

            {loading ? (
                <div className="mt-4 text-sm text-bl-muted">Loading notes…</div>
            ) : notes.length === 0 ? (
                <p className="mt-4 text-xs text-bl-muted">
                    Notes are private to your account. They're never shared with
                    contractors or via share links.
                </p>
            ) : (
                <ul className="mt-4 space-y-3" data-testid="investor-notes-list">
                    {notes.map((n) => {
                        const isEditing = editingId === n.note_id;
                        return (
                            <li
                                key={n.note_id}
                                className="bl-card p-3 md:p-4 bg-bl-bg/40 flex gap-3 items-start"
                                data-testid={`investor-note-${n.note_id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    {isEditing ? (
                                        <textarea
                                            value={editingText}
                                            onChange={(e) => setEditingText(e.target.value)}
                                            rows={2}
                                            className="w-full px-2 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                                            data-testid={`investor-note-edit-input-${n.note_id}`}
                                        />
                                    ) : (
                                        <p className="text-sm text-bl whitespace-pre-wrap leading-relaxed">
                                            {n.note}
                                        </p>
                                    )}
                                    <div className="mt-1 text-[10px] text-bl-muted font-mono-bl">
                                        {new Date(n.created_at).toLocaleString()}
                                        {n.updated_at && n.updated_at !== n.created_at && " · edited"}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {isEditing ? (
                                        <>
                                            <button
                                                onClick={() => onSaveEdit(n.note_id)}
                                                className="text-bl-primary p-1.5 rounded hover:bg-bl-teal-tint"
                                                aria-label="Save note"
                                                data-testid={`investor-note-save-${n.note_id}`}
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setEditingId(null);
                                                    setEditingText("");
                                                }}
                                                className="text-bl-muted p-1.5 rounded hover:bg-bl-bg"
                                                aria-label="Cancel edit"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setEditingId(n.note_id);
                                                    setEditingText(n.note);
                                                }}
                                                className="text-bl-muted p-1.5 rounded hover:bg-bl-teal-tint hover:text-bl-primary"
                                                aria-label="Edit note"
                                                data-testid={`investor-note-edit-${n.note_id}`}
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => onDelete(n.note_id)}
                                                className="text-bl-muted p-1.5 rounded hover:bg-[#C85B3A]/10 hover:text-[#C85B3A]"
                                                aria-label="Delete note"
                                                data-testid={`investor-note-delete-${n.note_id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
